import React, { useCallback, useEffect, useRef } from "react";
import AgGridTable from "components/AgGrid/AgGridContainer";
import { AgGridTables } from "constants/aggrid";
import { useDispatch, useSelector } from "react-redux";
import { ColumnType } from "components/AgGrid/constants";
import { Column } from "components/Layout";
import { CollapseTableWrapper } from "components/AgGrid/TableWrapper";
import { AgGridReact } from "ag-grid-react";
import { Tooltip } from "components/Form/FormikControls/Tooltip";
import {
  BusinessMergeEventsAction,
  BusinessUnMergeEventAction,
} from "actions/businessMergeEvents";
import { startCase } from "lodash";
import { toTitleCase } from "lib";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { selectBusinessMergeEventAggridData } from "selectors/businessMergeEvents";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { ApiModel } from "constants/loading";
import {
  ActionButton,
  ActionButtonContainer,
} from "components/AgGrid/ActionButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUndo } from "@fortawesome/pro-solid-svg-icons";
import { closeConfirmModal, openConfirmModal } from "actions";
import { BusinessMergeEventStatus } from "constants/businessMergeEvent";

type MergedBusinessAgGridRowType = {
  mergeEvent: BusinessMergeEvents & {
    actions: string;
  };
};

function MergedBusinessActionsColumnRenderer(
  props: ICellRendererParams<MergedBusinessAgGridRowType>,
) {
  const { data, node } = props;
  const { mergeEvent }: MergedBusinessAgGridRowType = data;
  const dispatch = useDispatch();

  const handleUnmergeClick = useCallback(() => {
    dispatch(
      openConfirmModal({
        title: `Confirm Unmerge of "${mergeEvent.sourceBusiness}"`,
        message:
          `Are you sure you want to unmerge "${mergeEvent.sourceBusiness}" from "${mergeEvent.targetBusiness}"?\n\n` +
          `NOTE: "${mergeEvent.sourceBusiness}" will be restored into view.`,
        actions: [
          {
            label: "No",
            secondary: true,
            onClick: () => {
              dispatch(closeConfirmModal());
            },
          },
          {
            label: "Yes",
            onClick: () => {
              dispatch(
                BusinessUnMergeEventAction.create(
                  {},
                  {
                    id: mergeEvent.id,
                    suppressToast: false,
                    suppressErrorToast: false,
                    suppressSuccessToast: false,
                    changeReason: undefined,
                    saleyardName: undefined,
                  },
                ),
              );
              dispatch(closeConfirmModal());
            },
          },
        ],
      }),
    );
  }, [dispatch, mergeEvent]);

  if (node.group) {
    return null;
  }

  return (
    <ActionButtonContainer>
      <ActionButton
        data-tour="action-edit-billing-run"
        onClick={handleUnmergeClick}
        type="button"
        disabled={mergeEvent.status !== BusinessMergeEventStatus.MERGED}
      >
        <FontAwesomeIcon icon={faUndo} />
        &nbsp;Unmerge
      </ActionButton>
    </ActionButtonContainer>
  );
}
const columnDefs: ColDef<MergedBusinessAgGridRowType>[] = [
  {
    colId: "2abeb731-5785-478a-a2ad-95392598a007",
    field: "mergeEvent.sourceBusiness",
    headerName: "Source Business",
    width: 100,
    type: ColumnType.STRING,
  },
  {
    colId: "e058ed02-9d9f-4b23-bada-bb2343708e4f",
    field: "mergeEvent.status",
    headerName: "Status",
    width: 100,
    type: ColumnType.STRING,
    cellRenderer: ({ value }: { value: string }) =>
      toTitleCase(startCase(value)),
  },
  {
    colId: "f28388ab-2200-4331-8a8b-34636e0eca4f",
    field: "mergeEvent.notes",
    headerName: "Notes",
    width: 100,
    type: ColumnType.STRING,
    cellRenderer: ({ value }: { value: string }) => {
      if (!value) {
        return "";
      }

      const details = (
        <>
          {value.split("\n").map(note => (
            <>
              {note}
              <br />
            </>
          ))}
        </>
      );
      return (
        <Tooltip title={details} placement="auto" fullWidth>
          <em>Hover over for details</em>
        </Tooltip>
      );
    },
  },
  {
    colId: "2b96a3c1-1f52-4dd8-b5d0-dc0a8df762a1",
    field: "mergeEvent.created",
    headerName: "Created",
    width: 100,
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },
  {
    colId: "73fc054e-6290-4897-8f95-1ca59cf88bd7",
    field: "mergeEvent.lastModified",
    headerName: "Last Modified",
    width: 100,
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },
  {
    colId: "3c2e5a7c-845b-4747-b2ce-22c4d53e6669",
    field: "mergeEvent.user",
    headerName: "User",
    width: 100,
    type: ColumnType.STRING,
  },
  {
    colId: "26bb68b1-7c6d-4ad2-803c-f9aaf53de2c9",
    field: "mergeEvent.actions",
    headerName: "Actions",
    width: 100,
    pinned: "right",
    cellRenderer: MergedBusinessActionsColumnRenderer,
  },
];

interface MergedBusinessesTableProps {
  businessId: string;
}

export const MergedBusinessesTable: React.FC<MergedBusinessesTableProps> = ({
  businessId,
}) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(BusinessMergeEventsAction.request({ id: businessId }));
  }, [dispatch, businessId]);

  const agGridInstance = useRef<AgGridReact>();

  const onGridReady = params => {
    agGridInstance.current = params;
  };

  const rowData = useSelector(selectBusinessMergeEventAggridData);

  return (
    <Column full>
      <WaitForSync requiredData={[ApiModel.BUSINESS_MERGE_EVENTS]}>
        <AgGridTable
          rowData={rowData}
          tableName={AgGridTables.BUSINESS_MERGE_EVENTS}
          getRowId={params => params.data.mergeEvent.id}
          showGlobalSearchFilter={false}
          context={{}}
          onGridReady={onGridReady}
          columnDefs={columnDefs}
          WrapperComponent={CollapseTableWrapper}
          hasExportCsvPermissions={false}
          hideSavedViews
        />
      </WaitForSync>
    </Column>
  );
};
